import React, { useCallback, useEffect, useState, JSX } from 'react';
import classNames from 'classnames';
import { IBoxComponent, IBoxRadioListComponent } from '@enuan/chat__utils';
import { cleanBoxProps } from '../../../utils/cleanBoxProps';
import { useDidUpdateEffect } from '@avalenti89/react-ts-utils';
import { FormRow } from './FormRow';
import { styled } from '@mui/material/styles';
import { fontSecondaryOblique } from 'src/theme/mixins';

type Props = IBoxComponent<
	IBoxRadioListComponent & Omit<IRadioItemProps, 'value' | 'onChange'>
> & {
	onChange?: (value: string) => void;
};

export const RadioList = styled(
	({
		// custom props
		type,
		help_label,
		error,
		error_label,
		required_label,
		label,
		onChange,
		group,

		// standard props
		id, //ignore it to avoid duplicate
		options,
		value = '',
		className,
		disabled,
		required,
		..._props
	}: Props) => {
		const props = cleanBoxProps(_props);
		const [_value, setValue] = useState(value);
		useDidUpdateEffect(() => {
			setValue(value);
		}, [value]);

		return (
			<FormRow
				className={classNames('form__radio-list', className)}
				label={label}
				error={error}
				error_label={error_label}
				help_label={help_label}
				required={required}
				required_label={required_label}
				{...props}
			>
				<div
					className={classNames(
						'radio-list',
						disabled && 'radio-list--disabled',
					)}
				>
					{options.map(({ value, label }, i) => (
						<RadioItem
							key={i}
							label={label}
							onChange={() => {
								onChange?.(value);
								setValue(value);
							}}
							checked={_value === value}
							group={group}
							disabled={disabled}
						/>
					))}
				</div>
			</FormRow>
		);
	},
	{
		label: 'RadioList',
		target: 'RadioList-root',
	},
)({
	'& .radio-list': {
		color: 'var(--form__radio-list_color)',
		'&.radio-list--disabled': {
			cursor: 'default',
		},
		'& .radio:not(:last-child)': {
			marginBottom: '0.3em',
		},
	},
	'& .radio': {
		display: 'flex',
		position: 'relative',
		alignItems: 'center',
		color: 'var(--form__radio-list__item__label_color)',
		'&:not(:first-child):not(:last-child)': {
			margin: '0.5em 0',
		},
		'& label': {
			margin: 0,
			...fontSecondaryOblique,
			fontSize: '1.5em',
			cursor: 'pointer',
		},
		'& input': {
			position: 'absolute',
			left: -9999,
			opacity: 0,
		},
		'& .icon': {
			width: '1.3em',
			height: '1.3em',
			cursor: 'pointer',
			marginRight: '0.5em',
			display: 'inline-block',
			'&.icon--circle': {
				borderRadius: '50%',
				border: '1px solid var(--form__radio-list__item__icon_border)',
				background: 'var(--form__radio-list__item__icon_background)',
			},
		},
		'&.radio--disabled': {
			cursor: 'default',
			color: 'var(--form__radio-list__item--disabled_color)',
		},
		'&.radio--checked': {
			color: 'var(--form__radio-list__item--checked__label_color)',
			'& .icon--circle': {
				background: 'var(--form__radio-list__item--checked__icon_background)',
				borderColor: 'var(--form__radio-list__item--checked__icon_border)',
			},
		},
	},
});

type IRadioItemProps = Omit<
	JSX.IntrinsicElements['input'],
	'onChange' | 'value' | 'type'
> & {
	onChange?: (value: boolean) => void;
	label: string;
	value?: JSX.IntrinsicElements['input']['checked'];
	group: string;
};
const RadioItem = ({
	group,
	checked,
	onChange,
	label,
	disabled,
	className,
	value,
	..._props
}: IRadioItemProps) => {
	const props = cleanBoxProps(_props);
	const val = checked ?? value ?? false;
	const [_checked, setChecked] = useState(val);
	useEffect(() => {
		setChecked(val);
	}, [val]);
	const _onChange = useCallback<React.ChangeEventHandler<HTMLInputElement>>(
		({ currentTarget: { checked } }) => {
			setChecked(checked);
			onChange?.(checked);
		},
		[onChange],
	);
	const handleClick = useCallback(() => {
		if (disabled) return;
		setChecked(true);
		onChange?.(true);
	}, [disabled, onChange]);

	return (
		<div
			className={classNames(
				className,
				'radio',
				_checked && 'radio--checked',
				disabled && 'radio--disabled',
			)}
		>
			<input
				className="input input--radio"
				type="radio"
				onChange={_onChange}
				name={group}
				checked={_checked}
				disabled={disabled}
				{...props}
			/>
			<i className="icon icon--circle" onClick={handleClick} />
			<label className={classNames('label')} onClick={handleClick}>
				{label}
			</label>
		</div>
	);
};
